// Sentry error / performance / replay reporting.
// Production-only; in dev we no-op so the SDK noise stays out of the console.
// DSN comes from VITE_SENTRY_DSN — leave unset to disable entirely.

import * as Sentry from '@sentry/react';

let initialized = false;

// Common false-positive error patterns that flood Sentry without telling us
// anything actionable. Adding to this list when we see a new noisy pattern
// in the Sentry dashboard. Each entry must be a substring match (case-sensitive).
const NOISE_PATTERNS = [
  // Triggered by Chrome dev-tools or browser extensions, not our code
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Cancelled fetches when user navigates away — expected, not an error
  'AbortError',
  'The user aborted a request',
  'Failed to fetch',
  // Browser extensions injecting into our pages
  'top.GLOBALS',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  // Network errors we can't fix from the client
  'NetworkError when attempting to fetch resource',
  'Load failed',
  // Cancelled by browser/OS (e.g. closing tab during fetch)
  'cancelled',
];

function isNoise(event) {
  const message = event?.message
    || event?.exception?.values?.[0]?.value
    || '';
  return NOISE_PATTERNS.some((p) => message.includes(p));
}

export function initSentry() {
  if (initialized) return;
  if (!import.meta.env.PROD) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // 10% of transactions are sampled for performance tracing.
    tracesSampleRate: 0.1,
    // Replay only on errors — full session replay would be wasteful here.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // Don't send PII — Inside Panama is a public editorial site.
    sendDefaultPii: false,
    // Drop the noisy false-positives BEFORE they hit Sentry's quota.
    // Returning null discards the event entirely (vs returning an empty
    // object which still counts against the plan).
    beforeSend(event) {
      if (isNoise(event)) return null;
      return event;
    },
  });

  initialized = true;
}

// Re-export the useful Sentry primitives so call sites don't import the SDK
// directly. If we ever swap providers, this is the only file that changes.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;
export const setUser = Sentry.setUser;
